import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, CalendarCheck } from "lucide-react";
import { Logo } from "./Logo";
import { NAV_LINKS } from "../lib/site";
import { useLead } from "./LeadModal";

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const { openLead } = useLead();

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      data-testid="site-header"
      className="fixed inset-x-0 top-0 z-50 border-b-2 border-gold-500/70 bg-white/95 shadow-[0_10px_34px_rgb(13,27,62,0.10)] backdrop-blur-xl"
    >
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-5 lg:px-10">
        <Link to="/" aria-label="Estate Paradise home">
          <Logo />
        </Link>

        <nav className="hidden items-center gap-8 lg:flex" data-testid="desktop-nav">
          {NAV_LINKS.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              data-testid={`nav-link-${link.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) =>
                `group relative text-[13px] font-bold uppercase tracking-[0.14em] transition-colors duration-300 ${
                  isActive ? "text-gold-600" : "text-navy-900 hover:text-gold-600"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {link.label}
                  <span
                    className={`absolute -bottom-2 left-0 h-0.5 bg-gold-500 transition-[width] duration-300 ${
                      isActive ? "w-full" : "w-0 group-hover:w-full"
                    }`}
                  />
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="schedule-site-visit-btn"
            onClick={() =>
              openLead({ title: "Schedule Your Site Visit", interest: "Schedule Site Visit", source: "Header CTA" })
            }
            className="group hidden items-center gap-2 bg-gold-500 px-5 py-3 text-[13px] font-bold uppercase tracking-[0.12em] text-navy-900 shadow-[0_10px_24px_rgb(197,160,89,0.35)] transition-[transform,box-shadow,background-color] duration-300 hover:-translate-y-0.5 hover:bg-gold-400 hover:shadow-[0_16px_32px_rgb(197,160,89,0.45)] sm:flex"
          >
            <CalendarCheck className="h-4 w-4 transition-transform duration-300 group-hover:-rotate-6" />
            Schedule Site Visit
          </button>
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setOpen(!open)}
            className="flex h-11 w-11 items-center justify-center border border-navy-900/15 text-navy-900 lg:hidden"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-navy-900/10 bg-white/95 backdrop-blur-xl lg:hidden"
            data-testid="mobile-nav"
          >
            <div className="space-y-1 px-6 py-6">
              {NAV_LINKS.map((link, i) => (
                <motion.div
                  key={link.to}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                >
                  <NavLink
                    to={link.to}
                    className={({ isActive }) =>
                      `block py-3 font-serif text-2xl font-light ${isActive ? "text-gold-600" : "text-navy-900"}`
                    }
                  >
                    {link.label}
                  </NavLink>
                </motion.div>
              ))}
              <button
                data-testid="mobile-schedule-visit-btn"
                onClick={() => {
                  setOpen(false);
                  openLead({ title: "Schedule Your Site Visit", interest: "Schedule Site Visit", source: "Header CTA" });
                }}
                className="mt-4 flex w-full items-center justify-center gap-2 bg-gold-500 px-5 py-4 text-sm font-bold uppercase tracking-[0.12em] text-navy-900"
              >
                <CalendarCheck className="h-4 w-4" />
                Schedule Site Visit
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
